import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { Skeleton } from './Feedback';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /** Label used on the mobile card (defaults to the header). */
  mobileLabel?: ReactNode;
  /** Leave out of the mobile card. */
  hideOnMobile?: boolean;
  /** Use as the mobile card's title (first primary column). */
  primary?: boolean;
  className?: string;
}

export interface TableProps<T> {
  /** Describes the table for screen readers (can be visually hidden). */
  caption: ReactNode;
  captionHidden?: boolean;
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  /** Shown instead of the table when there are no rows. */
  empty?: ReactNode;
  loading?: boolean;
  /** Optional per-row action cell (e.g. an Edit link) shown last / at the card bottom. */
  rowActions?: (row: T) => ReactNode;
  className?: string;
}

/**
 * Responsive data table: a real <table> from the `md` breakpoint, a list of cards on phones.
 * Money columns: `align: 'right'` and render `<Money pence=… />`.
 */
export function Table<T>({
  caption,
  captionHidden,
  columns,
  rows,
  getRowKey,
  empty,
  loading,
  rowActions,
  className,
}: TableProps<T>) {
  if (loading) {
    return (
      <div role="status" className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <span className="sr-only">Loading</span>
        <Skeleton lines={5} />
      </div>
    );
  }
  if (rows.length === 0 && empty) return <>{empty}</>;
  const primary = columns.find((c) => c.primary) ?? columns[0];
  return (
    <div className={className}>
      <div className="hidden overflow-x-auto rounded-[var(--radius-card)] border border-border bg-surface shadow-card md:block">
        <table className="w-full border-collapse text-left">
          <caption className={cn('p-3 text-left font-bold', captionHidden && 'sr-only')}>{caption}</caption>
          <thead className="bg-surface-muted">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={cn('border-b-2 border-border-strong px-3 py-2 font-bold', c.align === 'right' && 'text-right', c.className)}
                >
                  {c.header}
                </th>
              ))}
              {rowActions && (
                <th scope="col" className="border-b-2 border-border-strong px-3 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={getRowKey(row)} className="border-b border-border last:border-b-0 hover:bg-surface-muted">
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-3 py-3 align-top', c.align === 'right' && 'text-right tabular-nums', c.className)}>
                    {c.cell(row)}
                  </td>
                ))}
                {rowActions && <td className="px-3 py-3 text-right align-top">{rowActions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        <p className={cn('mb-2 font-bold', captionHidden && 'sr-only')}>{caption}</p>
        <ul className="flex flex-col gap-3">
          {rows.map((row) => (
            <li key={getRowKey(row)} className="rounded-[var(--radius-card)] border border-border bg-surface p-4 shadow-card">
              {primary && <div className="mb-2 text-lg font-bold">{primary.cell(row)}</div>}
              {/* Label and value share a line when they fit; otherwise the value wraps under its label (200% text). */}
              <dl className="flex flex-col gap-y-1">
                {columns
                  .filter((c) => c !== primary && !c.hideOnMobile)
                  .map((c) => (
                    <div key={c.key} className="flex flex-wrap items-baseline justify-between gap-x-4">
                      <dt className="min-w-0 text-muted">{c.mobileLabel ?? c.header}</dt>
                      <dd className={cn('ml-auto min-w-0 max-w-full text-right', c.align === 'right' && 'tabular-nums')}>{c.cell(row)}</dd>
                    </div>
                  ))}
              </dl>
              {rowActions && <div className="mt-3 flex flex-wrap gap-2">{rowActions(row)}</div>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
